import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import QRCodeSVG from 'react-qr-code';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { generateQRWithProfile } from '../../utils/qrCodeGenerator';
import { API_CONFIG } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Upload,
  QrCode as QrCodeIcon,
  Download,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Instagram,
  Linkedin,
  Github,
  Youtube,
  Globe,
  Link as LinkIcon,
  X as CloseIcon,
  Check,
  Palette,
  Copy,
  Send,
  Search,
  HelpCircle,
  Image as ImageIcon,
  Briefcase,
  Building2,
  TrendingUp,
  Code,
  Code2,
  Terminal,
  Target,
  DollarSign,
  BookOpen,
  Heart,
  GraduationCap,
  GitBranch,
  Rocket,
  FileText,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Star,
  ThumbsUp,
  MessageCircle,
  Share2,
  Bookmark,
  Tag,
  User,
  Users,
  ChevronDown,
  CreditCard
} from 'lucide-react';
import { PLAN_FEATURES } from '../../constants/subscriptions';
import GoogleBusinessIcon from '../../icons/social/google-business.svg?react';
import AmazonIcon from '../../icons/business/amazon.svg?react';
import FlipkartIcon from '../../icons/business/flipkart.svg?react';
import GeMIcon from '../../icons/business/gem.svg?react';
import IndiaMARTIcon from '../../icons/business/indiamart.svg?react';
import JustdialIcon from '../../icons/business/justdial.svg?react';
import GoogleAdsIcon from '../../icons/business/google-ads.svg?react';
import MetaAdsIcon from '../../icons/business/facebook-ads.svg?react';
import InstagramAdsIcon from '../../icons/business/instagram-ads.svg?react';
import WhatsAppBusinessIcon from '../../icons/business/whatsapp-business.svg?react';
import GoogleAnalyticsIcon from '../../icons/business/google-analytics.svg?react';

// Custom Facebook Icon Component
const FacebookIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Custom WhatsApp Icon Component
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488" />
  </svg>
);

const TwitterXIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 3h3.619l-7.884 9.058L22 21h-6.393l-4.613-5.414L5.6 21H2l8.533-9.823L2 3h6.393l4.193 4.923L18.244 3Z" />
  </svg>
);

// Types
interface UpiPayment {
  id?: number;
  name?: string;
  upi_id: string;
  upi_qr: string;
  display_order?: number;
  is_active?: number;
}

interface ProfileData {
  id: number;
  name: string;
  tagline: string;
  location: string;
  phone: string;
  secondary_phone?: string;
  email: string;
  bio: string;
  profile_image: string;
  profile_url: string;
  upi_id: string; // Keep for backward compatibility
  upi_qr: string; // Keep for backward compatibility
  upi_payments?: UpiPayment[]; // New: multiple UPI payments
  contact_form_title?: string;
  contact_form_button_text?: string;
  contact_form_enabled?: boolean;
  contact_form_email?: string;
  text_color?: 'white' | 'black';
  icon_shape?: 'circle' | 'rounded-square' | 'squircle' | 'glass-circle' | 'gradient-card';
  banner_shape?: 'circle' | 'rounded-square';
  right_panel_color?: string;
  theme_style?: 'theme1' | 'theme2' | 'theme3';
  links: UnifiedLink[];  // Unified links array - replaces social_links and custom_links
  collaborations: Collaboration[];
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
}

interface SocialLink {
  id: number;
  platform: string;
  label?: string;
  url: string;
  display_order: number;
}

interface CustomLink {
  id: number;
  label: string;
  url: string;
  icon: string;
  display_order: number;
}

const SPLIT_LAYOUT_THEMES = [
  { id: 'champagne', name: 'Champagne Elegance', bg: '#FDF6EC', left: '#F1E3D3', right: '#E6CBA8' },
  { id: 'lavender', name: 'Soft Royal Lavender', bg: '#F7F5FF', left: '#E6E0F8', right: '#C8B6E2' },
  { id: 'mint', name: 'Mint Luxury', bg: '#F2FBF7', left: '#DDF3EA', right: '#B7E4D4' },
  { id: 'ivory', name: 'Ivory & Rose Gold', bg: '#FFF8F5', left: '#F7E1D7', right: '#E8B4A0' },
];

const SIMPLE_CARD_THEMES = [
  { id: 'champagne', name: 'Champagne Elegance', bg: '#FDF6EC', card: '#E6CBA8' },
  { id: 'lavender', name: 'Soft Royal Lavender', bg: '#F7F5FF', card: '#C8B6E2' },
  { id: 'mint', name: 'Mint Luxury', bg: '#F2FBF7', card: '#B7E4D4' },
  { id: 'ivory', name: 'Ivory & Rose Gold', bg: '#FFF8F5', card: '#E8B4A0' },
];

interface Collaboration {
  id: number;
  logo: string;
  name: string;
  description: string;
  url: string;
  display_order: number;
}

interface CroppedImage {
  file: File;
  preview: string;
}

interface QRCodeStyle {
  fgColor: string;
  bgColor: string;
  size: number;
  logo?: string;
}

// Social platform configurations
const SOCIAL_PLATFORMS = [
  { key: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  { key: 'twitter', name: 'X (Twitter)', icon: TwitterXIcon, color: 'bg-black' },
  { key: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-gradient-to-r from-blue-600 to-blue-800' },
  { key: 'github', name: 'GitHub', icon: Github, color: 'bg-gradient-to-r from-gray-700 to-gray-900' },
  { key: 'youtube', name: 'YouTube', icon: Youtube, color: 'bg-gradient-to-r from-red-500 to-red-700' },
  { key: 'website', name: 'Website', icon: Globe, color: 'bg-gradient-to-r from-green-500 to-green-700' },
  { key: 'facebook', name: 'Facebook', icon: FacebookIcon, color: 'bg-gradient-to-r from-blue-600 to-blue-800' },
  { key: 'whatsapp', name: 'WhatsApp', icon: WhatsAppIcon, color: 'bg-gradient-to-r from-green-400 to-green-600' },
  { key: 'telegram', name: 'Telegram', icon: Send, color: 'bg-gradient-to-r from-blue-500 to-blue-700' },
  { key: 'google-business', name: 'Google My Business', icon: GoogleBusinessIcon, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
];

// Professional apps configuration
const PROFESSIONAL_APPS = [
  { key: 'naukri', name: 'Naukri', icon: Briefcase, iconName: 'Briefcase', color: 'bg-gradient-to-r from-blue-500 to-blue-700' },
  { key: 'indeed', name: 'Indeed', icon: Briefcase, iconName: 'Briefcase', color: 'bg-gradient-to-r from-indigo-500 to-indigo-700' },
  { key: 'glassdoor', name: 'Glassdoor', icon: Building2, iconName: 'Building2', color: 'bg-gradient-to-r from-green-500 to-green-700' },
  { key: 'angellist', name: 'AngelList', icon: TrendingUp, iconName: 'TrendingUp', color: 'bg-gradient-to-r from-purple-500 to-purple-700' },
  { key: 'stackoverflow', name: 'StackOverflow', icon: Code, iconName: 'Code', color: 'bg-gradient-to-r from-orange-500 to-orange-700' },
  { key: 'leetcode', name: 'LeetCode', icon: Code2, iconName: 'Code2', color: 'bg-gradient-to-r from-yellow-500 to-yellow-700' },
  { key: 'hackerrank', name: 'HackerRank', icon: Terminal, iconName: 'Terminal', color: 'bg-gradient-to-r from-green-600 to-green-800' },
  { key: 'behance', name: 'Behance', icon: Palette, iconName: 'Palette', color: 'bg-gradient-to-r from-blue-400 to-blue-600' },
  { key: 'dribbble', name: 'Dribbble', icon: Target, iconName: 'Target', color: 'bg-gradient-to-r from-pink-500 to-pink-700' },
  { key: 'fiverr', name: 'Fiverr', icon: DollarSign, iconName: 'DollarSign', color: 'bg-gradient-to-r from-green-500 to-green-700' },
  { key: 'adobe-portfolio', name: 'Adobe Portfolio', icon: ImageIcon, iconName: 'Image', color: 'bg-gradient-to-r from-red-500 to-red-700' },
  { key: 'medium', name: 'Medium', icon: BookOpen, iconName: 'BookOpen', color: 'bg-gradient-to-r from-gray-700 to-gray-900' },
  { key: 'pinterest', name: 'Pinterest', icon: Heart, iconName: 'Heart', color: 'bg-gradient-to-r from-red-500 to-red-700' },
  { key: 'canva', name: 'Canva Profile', icon: Palette, iconName: 'Palette', color: 'bg-gradient-to-r from-blue-400 to-blue-600' },
  { key: 'researchgate', name: 'ResearchGate', icon: GraduationCap, iconName: 'GraduationCap', color: 'bg-gradient-to-r from-green-600 to-green-800' },
  { key: 'google-scholar', name: 'Google Scholar', icon: BookOpen, iconName: 'BookOpen', color: 'bg-gradient-to-r from-blue-500 to-blue-700' },
  { key: 'gitlab', name: 'GitLab', icon: GitBranch, iconName: 'GitBranch', color: 'bg-gradient-to-r from-orange-500 to-orange-700' },
  { key: 'codepen', name: 'CodePen', icon: Code, iconName: 'Code', color: 'bg-gradient-to-r from-gray-800 to-gray-900' },
  { key: 'producthunt', name: 'Product Hunt', icon: Rocket, iconName: 'Rocket', color: 'bg-gradient-to-r from-orange-500 to-red-500' },
  { key: 'notion', name: 'Notion', icon: FileText, iconName: 'FileText', color: 'bg-gradient-to-r from-gray-700 to-gray-900' },
];

const BUSINESS_APPS = [
  { key: 'amazon', name: 'Amazon', icon: AmazonIcon, iconName: 'Amazon', color: 'bg-white border border-secondary-200' },
  { key: 'flipkart', name: 'Flipkart', icon: FlipkartIcon, iconName: 'Flipkart', color: 'bg-white border border-secondary-200' },
  { key: 'gem', name: 'GeM', icon: GeMIcon, iconName: 'GeM', color: 'bg-white border border-secondary-200' },
  { key: 'indiamart', name: 'IndiaMART', icon: IndiaMARTIcon, iconName: 'IndiaMART', color: 'bg-white border border-secondary-200' },
  { key: 'justdial', name: 'Justdial', icon: JustdialIcon, iconName: 'Justdial', color: 'bg-white border border-secondary-200' },
];

const MARKETING_APPS = [
  { key: 'google-ads', name: 'Google Ads', icon: GoogleAdsIcon, iconName: 'GoogleAds', color: 'bg-white border border-secondary-200' },
  { key: 'meta-ads', name: 'Meta Ads', icon: MetaAdsIcon, iconName: 'MetaAds', color: 'bg-white border border-secondary-200' },
  { key: 'instagram-ads', name: 'Instagram Ads', icon: InstagramAdsIcon, iconName: 'InstagramAds', color: 'bg-white border border-secondary-200' },
  { key: 'whatsapp-business', name: 'WhatsApp Business', icon: WhatsAppBusinessIcon, iconName: 'WhatsAppBusiness', color: 'bg-white border border-secondary-200' },
  { key: 'google-analytics', name: 'Google Analytics', icon: GoogleAnalyticsIcon, iconName: 'GoogleAnalytics', color: 'bg-white border border-secondary-200' },
];

// Icon registry for dynamic rendering
const ICON_REGISTRY: Record<string, React.ComponentType<{ className?: string }>> = {
  Briefcase,
  Building2,
  TrendingUp,
  Code,
  Code2,
  Terminal,
  Target,
  DollarSign,
  Image: ImageIcon,
  BookOpen,
  Heart,
  GraduationCap,
  GitBranch,
  Rocket,
  FileText,
  Link: LinkIcon,
  Mail: Mail,
  Phone: Phone,
  'MapPin': MapPin,
  Calendar: Calendar,
  Clock: Clock,
  Star: Star,
  ThumbsUp: ThumbsUp,
  MessageCircle: MessageCircle,
  Share: Share2,
  Bookmark: Bookmark,
  Tag: Tag,
  User: User,
  Users: Users,
  // Brand Icons
  Amazon: AmazonIcon,
  Flipkart: FlipkartIcon,
  GeM: GeMIcon,
  IndiaMART: IndiaMARTIcon,
  Justdial: JustdialIcon,
  GoogleAds: GoogleAdsIcon,
  MetaAds: MetaAdsIcon,
  InstagramAds: InstagramAdsIcon,
  WhatsAppBusiness: WhatsAppBusinessIcon,
  GoogleAnalytics: GoogleAnalyticsIcon,
};

// Unified Link Type - single source of truth for all links
interface UnifiedLink {
  id: number;
  kind: 'social' | 'custom';  // Replaces storageType/originalType
  url: string;
  display_order: number;

  // Metadata (preserved based on kind)
  platform?: string;  // Present when kind === 'social'
  label?: string;      // Present when kind === 'custom'
  icon?: string;       // Present when kind === 'custom'
}

// Sortable Unified Link Item
const SortableUnifiedLink: React.FC<{
  id: string;
  item: UnifiedLink;
  index: number;
  register: any;
  watch: any;
  removeLink: (index: number) => void;
}> = ({ id, item, index, register, watch, removeLink }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Render based on kind
  // Render social link
  if (item.kind === 'social') {
    const platformConfig = SOCIAL_PLATFORMS.find(p => p.key === item.platform);
    const IconComponent = platformConfig?.icon || LinkIcon;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex flex-col space-y-2 p-5 md:p-6 bg-white rounded-xl shadow-sm transition-all ${isDragging ? 'shadow-md scale-[1.02]' : ''
          }`}
      >
        {/* Row 1: Drag handle | Icon | Input */}
        <div className="flex items-center gap-3">
          {/* Drag handle - fixed width container */}
          <div
            {...attributes}
            {...listeners}
            className="w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none select-none min-h-[44px] md:min-h-0 flex-shrink-0"
          >
            <GripVertical className="h-4 w-4" />
          </div>

          {/* App icon */}
          <div className={`w-10 h-10 ${platformConfig?.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <IconComponent className="h-5 w-5 text-white" />
          </div>

          {/* Input field - takes remaining width */}
          <input
            {...register(`links.${index}.url`, {
              pattern: {
                value: /^https?:\/\/.+/,
                message: 'URL must start with http:// or https://'
              }
            })}
            type="url"
            placeholder={`Enter ${platformConfig?.name} URL`}
            className="flex-1 min-w-0 border-0 border-b border-gray-200 focus:border-primary-500 focus:ring-0 bg-transparent py-2 text-sm placeholder:text-gray-400 transition-colors"
          />
        </div>

        {/* Row 2: Label | Delete - aligned with content column */}
        <div className="flex items-center gap-3">
          {/* Spacer matching drag handle width */}
          <div className="w-8 flex-shrink-0"></div>

          {/* Spacer matching icon width */}
          <div className="w-10 flex-shrink-0"></div>

          {/* Label text - aligns with input start */}
          <input
            {...register(`links.${index}.label`)}
            type="text"
            placeholder={platformConfig?.name || 'Social link'}
            className="flex-1 min-w-0 border-0 border-b border-gray-200 focus:border-primary-500 focus:ring-0 bg-transparent py-1 text-sm text-gray-600 placeholder:text-gray-400 transition-colors"
          />

          {/* Delete button - aligns with card right edge */}
          <button
            type="button"
            onClick={() => removeLink(index)}
            className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1 min-h-[44px] md:min-h-0 transition-colors flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>
    );
  }

  // Render custom link
  if (item.kind === 'custom') {
    const iconName = watch(`links.${index}.icon`) || item.icon || 'Link';
    const label = watch(`links.${index}.label`) || item.label || '';
    const IconComponent = ICON_REGISTRY[iconName] || LinkIcon;

    // Find app config to get color, or use default
    const allApps = [...PROFESSIONAL_APPS, ...BUSINESS_APPS, ...MARKETING_APPS];
    const appConfig = allApps.find(app => app.iconName === iconName);
    const colorClass = appConfig?.color || 'bg-gradient-to-r from-primary-500 to-primary-700';
    const isJustdial = iconName === 'Justdial';

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex flex-col space-y-2 p-5 md:p-6 bg-white rounded-xl shadow-sm transition-all ${isDragging ? 'shadow-md scale-[1.02]' : ''
          }`}
      >
        {/* Row 1: Drag handle | Icon | Input */}
        <div className="flex items-center gap-3">
          {/* Drag handle - fixed width container */}
          <div
            {...attributes}
            {...listeners}
            className="w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none select-none min-h-[44px] md:min-h-0 flex-shrink-0"
          >
            <GripVertical className="h-4 w-4" />
          </div>

          {/* App icon */}
          <div className={`w-10 h-10 ${colorClass} rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 ${isJustdial ? 'p-1' : 'p-1.5'}`}>
            {isJustdial ? (
              <IconComponent className="w-full h-auto max-h-[60%] object-contain" />
            ) : (
              <IconComponent className="h-full w-full object-contain" />
            )}
          </div>

          {/* Input field - takes remaining width */}
          <input
            {...register(`links.${index}.url`, {
              pattern: {
                value: /^https?:\/\/.+/,
                message: 'URL must start with http:// or https://'
              }
            })}
            type="url"
            placeholder={label ? `Enter your ${label} URL` : 'Enter URL'}
            className="flex-1 min-w-0 border-0 border-b border-gray-200 focus:border-primary-500 focus:ring-0 bg-transparent py-2 text-sm placeholder:text-gray-400 transition-colors"
          />
        </div>

        {/* Row 2: Label | Delete - aligned with content column */}
        <div className="flex items-center gap-3">
          {/* Spacer matching drag handle width */}
          <div className="w-8 flex-shrink-0"></div>

          {/* Spacer matching icon width */}
          <div className="w-10 flex-shrink-0"></div>

          {/* Label text - aligns with input start */}
          <p className="text-sm text-gray-500 flex-1 min-w-0 truncate">Custom link</p>

          {/* Delete button - aligns with card right edge */}
          <button
            type="button"
            onClick={() => removeLink(index)}
            className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1 min-h-[44px] md:min-h-0 transition-colors flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Sortable Collaboration Item
const SortableCollaboration: React.FC<{
  id: string;
  index: number;
  register: any;
  watch: any;
  setValue: any;
  removeCollaboration: (index: number) => void;
  handleCollaborationLogoUpload: (file: File, index: number) => void;
}> = ({ id, index, register, watch, setValue, removeCollaboration, handleCollaborationLogoUpload }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 bg-white border rounded-lg transition-colors ${isDragging ? 'shadow-lg border-primary-300' : 'border-secondary-200'
        }`}
    >
      <div className="flex items-start space-x-3">
        <div
          {...attributes}
          {...listeners}
          className="text-secondary-400 hover:text-secondary-600 mt-2 cursor-grab active:cursor-grabbing touch-none select-none"
        >
          <GripVertical className="h-5 w-5" />
        </div>

        <div className="flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Logo
              </label>
              {watch(`collaborations.${index}.logo`) ? (
                <div className="mb-2">
                  <div className="relative inline-block">
                    <img
                      src={convertToAbsoluteImageUrl(watch(`collaborations.${index}.logo`))}
                      alt="Logo preview"
                      className="w-16 h-16 object-cover rounded-lg border border-secondary-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setValue(`collaborations.${index}.logo` as const, '');
                      }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">
                    Logo uploaded
                  </p>
                </div>
              ) : null}
              <input
                type="file"
                accept="image/*"
                className="input-field"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleCollaborationLogoUpload(file, index);
                    // Reset the file input after selection
                    e.target.value = '';
                  }
                }}
              />
              <p className="text-xs text-secondary-500 mt-1">
                Max 1MB - JPG, PNG, WEBP
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Name/Title
              </label>
              <input
                {...register(`collaborations.${index}.name`)}
                type="text"
                placeholder="Company or project name"
                className="input-field"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Description
            </label>
            <textarea
              {...register(`collaborations.${index}.description`)}
              rows={2}
              placeholder="Brief description of the collaboration"
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              External Link
            </label>
            <input
              {...register(`collaborations.${index}.url`, {
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'URL must start with http:// or https://'
                }
              })}
              type="url"
              placeholder="https://..."
              className="input-field"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => removeCollaboration(index)}
          className="text-red-500 hover:text-red-700 mt-2"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// App Selector Modal Component
const AppSelectorModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSelectApp: (app: { name: string, iconName: string }) => void;
  onSelectCustom: () => void;
}> = ({ isOpen, onClose, onSelectApp, onSelectCustom }) => {
  if (!isOpen) return null;

  const renderAppGrid = (title: string, apps: any[]) => (
    <div className="mb-8 last:mb-0">
      <h4 className="text-xs font-bold text-secondary-400 uppercase tracking-widest mb-4 px-1">{title}</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {apps.map((app) => {
          const IconComponent = app.icon;
          const isJustdial = app.key === 'justdial' || app.iconName === 'Justdial';
          return (
            <button
              key={app.key}
              type="button"
              onClick={() => {
                onSelectApp(app);
                onClose();
              }}
              className="flex flex-col items-center justify-center p-4 border border-secondary-200 rounded-xl hover:border-primary-400 hover:bg-primary-50 transition-all group shadow-sm hover:shadow-md"
            >
              <div className={`w-12 h-12 ${app.color} rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-200 overflow-hidden ${isJustdial ? 'p-1' : 'p-2'}`}>
                {isJustdial ? (
                  <IconComponent className="w-full h-auto max-h-[60%] object-contain" />
                ) : (
                  <IconComponent className="h-full w-full object-contain" />
                )}
              </div>
              <span className="text-[11px] text-secondary-700 text-center font-bold leading-tight">{app.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="p-6 border-b border-secondary-100 flex items-center justify-between bg-secondary-50/50">
          <div>
            <h3 className="text-xl font-bold text-secondary-900">Select App</h3>
            <p className="text-xs text-secondary-500 mt-0.5">Choose from predefined apps or add your own</p>
          </div>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600 p-2 hover:bg-white rounded-full transition-colors"
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          <div className="space-y-2">
            {renderAppGrid("Business Platforms", BUSINESS_APPS)}
            {renderAppGrid("Marketing & Ads", MARKETING_APPS)}
            {renderAppGrid("Professional Apps", PROFESSIONAL_APPS)}

            {/* Custom App Option */}
            <div className="mt-8 pt-6 border-t border-secondary-100">
              <button
                type="button"
                onClick={() => {
                  onSelectCustom();
                  onClose();
                }}
                className="w-full flex items-center justify-center p-5 border-2 border-dashed border-secondary-200 rounded-xl hover:border-primary-400 hover:bg-primary-50 transition-all group"
              >
                <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-primary-100 transition-colors">
                  <Plus className="h-6 w-6 text-secondary-600 group-hover:text-primary-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-secondary-900">Add Custom App</p>
                  <p className="text-xs text-secondary-500">Add any other platform with a custom label</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom App Selector Component
const CustomAppSelector: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onAdd: (label: string, iconName: string) => void;
}> = ({ isOpen, onClose, onAdd }) => {
  const [label, setLabel] = useState('');
  const [selectedIconName, setSelectedIconName] = useState('Link');

  // Popular Lucide icons for custom selection
  const availableIcons = [
    { name: 'Link', component: LinkIcon },
    { name: 'Mail', component: Mail },
    { name: 'Phone', component: Phone },
    { name: 'MapPin', component: MapPin },
    { name: 'Calendar', component: Calendar },
    { name: 'Clock', component: Clock },
    { name: 'Star', component: Star },
    { name: 'Heart', component: Heart },
    { name: 'ThumbsUp', component: ThumbsUp },
    { name: 'MessageCircle', component: MessageCircle },
    { name: 'Share', component: Share2 },
    { name: 'Bookmark', component: Bookmark },
    { name: 'Tag', component: Tag },
    { name: 'User', component: User },
    { name: 'Users', component: Users },
    { name: 'Briefcase', component: Briefcase },
    { name: 'Building2', component: Building2 },
    { name: 'Code', component: Code },
    { name: 'Palette', component: Palette },
    { name: 'Rocket', component: Rocket },
  ];

  const handleAdd = () => {
    if (label.trim()) {
      onAdd(label.trim(), selectedIconName);
      setLabel('');
      setSelectedIconName('Link');
      onClose();
    }
  };

  if (!isOpen) return null;

  const SelectedIconComponent = ICON_REGISTRY[selectedIconName] || LinkIcon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-secondary-900">Add Custom App</h3>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Label Input */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                App Name / Label
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Enter app name or label"
                className="input-field w-full"
              />
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Select Icon
              </label>
              <div className="grid grid-cols-5 sm:grid-cols-8 gap-3">
                {availableIcons.map((icon) => {
                  const IconComponent = icon.component;
                  const isSelected = selectedIconName === icon.name;
                  return (
                    <button
                      key={icon.name}
                      type="button"
                      onClick={() => setSelectedIconName(icon.name)}
                      className={`p-3 border-2 rounded-lg transition-colors ${isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-secondary-200 hover:border-secondary-300'
                        }`}
                    >
                      <IconComponent className="h-5 w-5 text-secondary-700 mx-auto" />
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-center">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                  <SelectedIconComponent className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={!label.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to convert relative image URLs to absolute URLs
const convertToAbsoluteImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return '';
  // Already absolute (http/https or blob URLs)
  if (imageUrl.startsWith('http') || imageUrl.startsWith('blob:')) return imageUrl;
  // Convert /uploads/profiles/xxx.jpg to /backend/image.php?file=xxx.jpg&type=profiles
  if (imageUrl.startsWith('/uploads/profiles/')) {
    const filename = imageUrl.split('/').pop();
    return `${API_CONFIG.BASE_URL}/image.php?file=${filename}&type=profiles`;
  }
  // Convert /uploads/qr/xxx.jpg to /backend/image.php?file=xxx.jpg&type=qr
  if (imageUrl.startsWith('/uploads/qr/')) {
    const filename = imageUrl.split('/').pop();
    return `${API_CONFIG.BASE_URL}/image.php?file=${filename}&type=qr`;
  }
  // Convert /api/uploads/collaborations/xxx.jpg to /backend/image.php?file=xxx.jpg&type=collaborations
  if (imageUrl.startsWith('/api/uploads/collaborations/')) {
    const filename = imageUrl.split('/').pop();
    return `${API_CONFIG.BASE_URL}/image.php?file=${filename}&type=collaborations`;
  }
  // image.php?file=xxx&type=qr (relative) - prepend API base
  if (imageUrl.startsWith('/image.php') || imageUrl.startsWith('image.php')) {
    const path = imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl;
    return `${API_CONFIG.BASE_URL.replace(/\/$/, '')}${path}`;
  }
  // Return as-is for other relative URLs
  return imageUrl;
};

const Profile: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeSvg, setQrCodeSvg] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [croppedImage, setCroppedImage] = useState<CroppedImage | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [qrCodeStyle, setQrCodeStyle] = useState<QRCodeStyle>({
    fgColor: '#000000',
    bgColor: '#FFFFFF',
    size: 256
  });
  const [showAppSelector, setShowAppSelector] = useState(false);
  const [showCustomAppSelector, setShowCustomAppSelector] = useState(false);

  // Appearance customization state
  const [bgColor, setBgColor] = useState('#FDF6EC');
  const [bgImage, setBgImage] = useState('');
  const [cardColor, setCardColor] = useState('#E6CBA8');
  const [rightPanelColor, setRightPanelColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState<'white' | 'black'>('white');
  const [iconShape, setIconShape] = useState<string>('circle');
  const [bannerShape, setBannerShape] = useState<string>('circle');
  const [themeStyle, setThemeStyle] = useState<string>('theme2');
  const [uploadingBg, setUploadingBg] = useState(false);

  const { setProfileImage, subscription } = useAuth();
  const activePlan = subscription?.plan ?? 'free';
  const allowedFeatures = PLAN_FEATURES[activePlan] ?? PLAN_FEATURES.free;
  const hasFeature = (feature: string) => allowedFeatures.includes(feature);

  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const isPaidPlan = subscription?.plan === 'basic' || subscription?.plan === 'premium';

  // Section navigation state
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Scroll to top when section changes
  useEffect(() => {
    if (activeSection !== null) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeSection]);

  // PWA Install prompt capture
  useEffect(() => {
    // Check if prompt was already captured before React loaded
    if ((window as any).deferredInstallPrompt) {
      setDeferredPrompt((window as any).deferredInstallPrompt);
      setIsInstallable(true);
    }

    const handlePwaInstallReady = () => {
      if ((window as any).deferredInstallPrompt) {
        setDeferredPrompt((window as any).deferredInstallPrompt);
        setIsInstallable(true);
      }
    };

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      (window as any).deferredInstallPrompt = e;
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
      (window as any).deferredInstallPrompt = null;
    };

    window.addEventListener('pwainstallready', handlePwaInstallReady);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwainstallready', handlePwaInstallReady);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  // Contact Form Subjects Management
  const [contactFormSubjects, setContactFormSubjects] = useState<string[]>([]);
  const [newSubject, setNewSubject] = useState('');

  // Load subjects from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('contact_form_subjects');
      if (stored) {
        setContactFormSubjects(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load contact form subjects:', error);
    }
  }, []);

  // Save subjects to localStorage when they change
  const saveSubjectsToLocalStorage = (subjects: string[]) => {
    localStorage.setItem('contact_form_subjects', JSON.stringify(subjects));
    setContactFormSubjects(subjects);
  };

  const addSubject = () => {
    if (newSubject.trim() && !contactFormSubjects.includes(newSubject.trim())) {
      const updated = [...contactFormSubjects, newSubject.trim()];
      saveSubjectsToLocalStorage(updated);
      setNewSubject('');
    }
  };

  const removeSubject = (subject: string) => {
    const updated = contactFormSubjects.filter(s => s !== subject);
    saveSubjectsToLocalStorage(updated);
  };

  // Contact Form Popup Delay Management
  const [popupDelay, setPopupDelay] = useState(10); // Default 10 seconds

  // Load popup delay from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('contact_form_popup_delay');
      if (stored) {
        const delay = parseInt(stored, 10);
        if (delay >= 5 && delay <= 15) {
          setPopupDelay(delay);
        }
      }
    } catch (error) {
      console.error('Failed to load popup delay:', error);
    }
  }, []);

  const updatePopupDelay = (delay: number) => {
    setPopupDelay(delay);
    localStorage.setItem('contact_form_popup_delay', delay.toString());
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { register, handleSubmit, control, setValue, reset, watch, getValues, formState: { errors } } = useForm<ProfileData>({
    defaultValues: {
      name: '',
      tagline: '',
      location: '',
      phone: '',
      secondary_phone: '',
      email: '',
      bio: '',
      profile_image: '',
      profile_url: '',
      upi_id: '',
      upi_qr: '',
      upi_payments: [], // New: multiple UPI payments
      contact_form_title: '',
      contact_form_button_text: '',
      contact_form_enabled: true,
      contact_form_email: '',
      text_color: 'white',
      icon_shape: 'circle',
      banner_shape: 'circle',
      right_panel_color: '#ffffff',
      theme_style: 'theme2',
      links: [],  // Unified links array - replaces social_links and custom_links
      collaborations: [],
      seo_title: '',
      seo_description: '',
      seo_keywords: ''
    }
  });

  const buildProfileUrl = useCallback(
    (
      slug: string | undefined,
      options: { legacy?: boolean; variant?: 'connect' | 'user' | 'u'; relative?: boolean } = {}
    ) => {
      const { legacy = false, variant, relative = false } = options;
      const safeSlug = (slug ?? '').trim();
      let segment: 'connect' | 'user' | 'u' | '';

      if (variant) {
        segment = variant;
      } else if (legacy) {
        segment = 'u';
      } else {
        // Default to root level - no segment
        segment = '';
      }

      const path = segment ? (safeSlug ? `${segment}/${safeSlug}` : segment) : safeSlug;
      if (relative) {
        return `/${path}`;
      }
      return `${window.location.origin}/${path}`;
    },
    []
  );

  // Helper function to format URL for display only (UI-only change)
  // Displays as: https://domain/{username} instead of https://myoneqr.com/connect/{username}
  const formatDisplayUrl = useCallback((url: string, username: string | undefined): string => {
    if (!url || !username) return url;
    const origin = window.location.origin; // Gets http://localhost:5173 or https://myoneqr.com
    return `${origin}/${username}`;
  }, []);

  const profileSlug = watch('profile_url');
  const previewName = watch('name')?.trim() || 'Your Name';
  const previewTagline = watch('tagline')?.trim() || 'Your tagline here';
  const previewBio = watch('bio')?.trim() || 'Write a short bio to preview your public card style.';
  const previewLocation = watch('location')?.trim() || 'Your location';
  const previewLinks = (watch('links') || []).slice(0, 5);
  const previewCollaborations = (watch('collaborations') || []).filter((c: any) => c.name && c.name.trim()).slice(0, 3);
  const isPreviewTextBlack = (themeStyle === 'theme1' || themeStyle === 'theme2') ? true : textColor === 'black';
  const previewTextPrimary = isPreviewTextBlack ? '#000000' : '#ffffff';
  const previewTextMuted = isPreviewTextBlack ? 'rgba(0,0,0,0.68)' : 'rgba(255,255,255,0.68)';
  const previewTextSoft = isPreviewTextBlack ? 'rgba(0,0,0,0.52)' : 'rgba(255,255,255,0.52)';
  const previewBgImage = bgImage
    ? (bgImage.startsWith('http') ? bgImage : `${API_CONFIG.BASE_URL}${bgImage}`)
    : '';
  const previewIconShapeClass = iconShape === 'rounded-square'
    ? 'rounded-xl'
    : iconShape === 'squircle'
      ? 'rounded-[1.1rem]'
      : 'rounded-full';
  const previewIconStyle = iconShape === 'glass-circle'
    ? { backgroundColor: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.34)', backdropFilter: 'blur(8px)' }
    : iconShape === 'gradient-card'
      ? { backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.24), rgba(255,255,255,0.08))', border: '1px solid rgba(255,255,255,0.24)' }
      : { backgroundColor: 'rgba(255,255,255,0.12)' };
  const primaryProfileUrl = useMemo(() => (
    profileSlug ? buildProfileUrl(profileSlug) : ''
  ), [buildProfileUrl, profileSlug]);

  const userProfileUrl = useMemo(() => (
    profileSlug ? buildProfileUrl(profileSlug, { variant: 'user' }) : ''
  ), [buildProfileUrl, profileSlug]);

  const legacyProfileUrl = useMemo(() => (
    profileSlug ? buildProfileUrl(profileSlug, { variant: 'u' }) : ''
  ), [buildProfileUrl, profileSlug]);

  const { fields: linkFields, append: appendLink, remove: removeLink, move: moveLink } = useFieldArray({
    control,
    name: 'links'
  });

  const { fields: collaborationFields, append: appendCollaboration, remove: removeCollaboration, move: moveCollaboration } = useFieldArray({
    control,
    name: 'collaborations'
  });

  const { fields: upiPaymentFields, append: appendUpiPayment, remove: removeUpiPayment } = useFieldArray({
    control,
    name: 'upi_payments'
  });

  // No longer needed - linkFields is already unified

  // Define generateQRCode BEFORE useEffect that uses it
  const generateQRCode = useCallback(async (profileUrl: string) => {
    if (!profileUrl) return;

    try {
      const url = buildProfileUrl(profileUrl) + '?source=qr';

      // profileImageUrl is already converted to absolute URL by convertToAbsoluteImageUrl
      const absoluteImageUrl = profileImageUrl || undefined;

      // Generate QR with profile image overlay
      const compositeQR = await generateQRWithProfile(url, absoluteImageUrl, {
        size: qrCodeStyle.size,
        fgColor: qrCodeStyle.fgColor,
        bgColor: qrCodeStyle.bgColor,
        logoSize: 30, // 30% of QR size
        logoBorderWidth: 3
      });

      setQrCodeUrl(compositeQR);

      // Generate SVG QR code
      setQrCodeSvg(url);
    } catch (error: any) {
      console.error('Failed to generate QR code:', error);
      // Don't show toast for QR code errors as it's not critical
      setQrCodeSvg(buildProfileUrl(profileUrl, { variant: 'user' }));
    }
  }, [buildProfileUrl, profileImageUrl, qrCodeStyle]);

  // Load profile data - reload when navigating back to this tab
  useEffect(() => {
    // Ensure loading is set even if fetchProfile fails
    let mounted = true;

    const loadProfile = async () => {
      try {
        await fetchProfile();
      } catch (err) {
        console.error('Unexpected error in fetchProfile:', err);
        if (mounted) {
          setError('Unexpected error loading profile');
          setHasError(true);
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      mounted = false;
    };
    // Reload when location changes (navigating back to profile tab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Generate QR code when profile URL or profile image changes
  const profileUrl = watch('profile_url');
  useEffect(() => {
    if (profileUrl) {
      const timer = setTimeout(() => {
        generateQRCode(profileUrl);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [profileUrl, profileImageUrl, generateQRCode]);

  // Set image loaded when cropper image changes
  useEffect(() => {
    if (croppedImage) {
      const img = new Image();
      img.onload = () => {
        setImageLoaded(true);
      };
      img.src = croppedImage.preview;
    }
  }, [croppedImage]);

  const fetchProfile = async () => {
    try {
      setError(null);
      setHasError(false);
      const response = await api.get('/profiles/me');

      console.log('Profile API Response:', response.data);

      if (response.data && response.data.success) {
        const profileData = response.data.profile;

        // Log the received data to help debug
        console.log('Profile Data Received:', profileData);

        // Ensure profileData exists and is an object
        if (!profileData || typeof profileData !== 'object') {
          console.error('Invalid profile data received:', profileData);
          setError('Invalid profile data received from server');
          setHasError(true);
          setLoading(false);
          return;
        }

        try {
          // Transform social_links and custom_links to unified links[]
          const unifiedLinks: UnifiedLink[] = [];

          // Add social links
          if (Array.isArray(profileData.social_links)) {
            profileData.social_links.forEach((link: SocialLink) => {
              unifiedLinks.push({
                id: link.id,
                kind: 'social',
                url: link.url,
                display_order: link.display_order,
                platform: link.platform,
                label: link.label
              });
            });
          }

          // Add custom links
          if (Array.isArray(profileData.custom_links)) {
            profileData.custom_links.forEach((link: CustomLink) => {
              unifiedLinks.push({
                id: link.id,
                kind: 'custom',
                url: link.url,
                display_order: link.display_order,
                label: link.label,
                icon: link.icon
              });
            });
          }

          // Sort by display_order to maintain backend order
          unifiedLinks.sort((a, b) => a.display_order - b.display_order);

          // Use reset() to properly initialize all form fields including nested arrays
          // Handle null/undefined values safely
          reset({
            name: profileData.name ?? '',
            tagline: profileData.tagline ?? '',
            location: profileData.location ?? '',
            phone: profileData.phone ?? '',
            secondary_phone: profileData.secondary_phone ?? '',
            email: profileData.email ?? '',
            bio: profileData.bio ?? '',
            profile_image: profileData.profile_image ?? '',
            profile_url: profileData.profile_url ?? '',
            upi_id: profileData.upi_id ?? '',
            upi_qr: profileData.upi_qr ?? '',
            upi_payments: Array.isArray(profileData.upi_payments) && profileData.upi_payments.length > 0
              ? profileData.upi_payments
              : (profileData.upi_id ? [{ name: '', upi_id: profileData.upi_id, upi_qr: profileData.upi_qr ?? '' }] : []),
            contact_form_title: profileData.contact_form_title ?? '',
            contact_form_button_text: profileData.contact_form_button_text ?? '',
            contact_form_enabled: profileData.contact_form_enabled ?? true,
            contact_form_email: profileData.contact_form_email ?? '',
            text_color: profileData.text_color === 'black' ? 'black' : 'white',
            icon_shape: profileData.icon_shape || 'circle',
            banner_shape: profileData.banner_shape || 'circle',
            right_panel_color: profileData.right_panel_color || '#ffffff',
            theme_style: profileData.theme_style || 'theme2',
            links: unifiedLinks,
            collaborations: Array.isArray(profileData.collaborations) ? profileData.collaborations : [],
            seo_title: profileData.seo_title ?? '',
            seo_description: profileData.seo_description ?? '',
            seo_keywords: profileData.seo_keywords ?? ''
          });
        } catch (resetError: any) {
          console.error('Error resetting form:', resetError);
          setError('Failed to initialize form. Please refresh the page.');
          setHasError(true);
          toast.error('Failed to initialize form');
        }

        // Construct full URL for existing profile image
        try {
          const fullImageUrl = convertToAbsoluteImageUrl(profileData.profile_image ?? '');
          setProfileImageUrl(fullImageUrl);
          setProfileImage(fullImageUrl || null);
        } catch (imgError) {
          console.error('Error setting profile image:', imgError);
        }

        // Set appearance customization fields
        try {
          setBgColor(profileData.bg_color ?? '#f5c6aa');
          setBgImage(profileData.bg_image ?? '');
          setCardColor(profileData.card_color ?? '#4a4a4a');
          setTextColor(profileData.text_color === 'black' ? 'black' : 'white');
          setIconShape(profileData.icon_shape || 'circle');
          setBannerShape(profileData.banner_shape || 'circle');
          setRightPanelColor(profileData.right_panel_color || '#ffffff');
          setThemeStyle(profileData.theme_style || 'theme2');
        } catch (appearanceError) {
          console.error('Error setting appearance fields:', appearanceError);
        }

        // UPI payments are now loaded from profileData.upi_payments array
        // No need to set upiQRPreview since each payment has its own QR

        // Generate QR code will be triggered by useEffect when profileUrl changes
      } else {
        const errorMsg = response.data?.message || 'Unknown error';
        setError('Failed to load profile: ' + errorMsg);
        setHasError(true);
        toast.error(errorMsg || 'Failed to load profile data');
      }
    } catch (error: any) {
      console.error('Failed to fetch profile:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load profile data';
      setError(errorMessage);
      setHasError(true);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Image upload with drag & drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload JPG, JPEG, PNG, or WEBP files only');
        return;
      }

      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }

      // Show cropper
      const reader = new FileReader();
      reader.onload = () => {
        setCroppedImage({
          file,
          preview: reader.result as string
        });
        setShowCropper(true);
        setImageLoaded(false);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    multiple: false
  });

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);

    // Ensure image is fully loaded
    if (image.naturalWidth === 0 || image.naturalHeight === 0) {
      throw new Error('Image not fully loaded');
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set canvas dimensions to crop size
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image (rectangular, no circular mask)
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Failed to create blob');
        }
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleCropComplete = async () => {
    if (!croppedImage || !croppedAreaPixels) return;

    try {
      const croppedImageBlob = await getCroppedImg(croppedImage.preview, croppedAreaPixels);

      const formData = new FormData();
      formData.append('image', croppedImageBlob, 'profile.jpg');

      const response = await api.post('/upload/profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // Construct full URL for the image using the image serving script
        const fullImageUrl = convertToAbsoluteImageUrl(response.data.image_url);
        setProfileImageUrl(fullImageUrl);
        setProfileImage(fullImageUrl || null);
        setValue('profile_image', response.data.image_url); // Store original URL in form
        setShowCropper(false);
        setCroppedImage(null);
        setImageLoaded(false);
        toast.success('Profile image uploaded successfully');
      } else {
        toast.error(response.data.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    }
  };

  // Social links management
  const addSocialLink = (platform: string) => {
    appendLink({
      id: Date.now(),
      kind: 'social',
      platform,
      url: '',
      display_order: linkFields.length
    });
  };

  // Custom links management
  const addCustomLink = (app?: { name: string, iconName: string }, customLabel?: string, customIconName?: string) => {
    if (app) {
      // Add from predefined app
      appendLink({
        id: Date.now(),
        kind: 'custom',
        label: app.name,
        icon: app.iconName,
        url: '',
        display_order: linkFields.length
      });
    } else if (customLabel && customIconName) {
      // Add custom app
      appendLink({
        id: Date.now(),
        kind: 'custom',
        label: customLabel,
        icon: customIconName,
        url: '',
        display_order: linkFields.length
      });
    }
  };

  // Collaborations management
  const addCollaboration = () => {
    appendCollaboration({
      id: Date.now(),
      logo: '',
      name: '',
      description: '',
      url: '',
      display_order: collaborationFields.length
    });
  };

  // Drag and drop handlers
  // Simplified: Only reorders items in unified links array
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    // Handle collaborations separately
    if (activeId.startsWith('collaboration-')) {
      const oldIndex = collaborationFields.findIndex(item => `collaboration-${item.id}` === activeId);
      const newIndex = collaborationFields.findIndex(item => `collaboration-${item.id}` === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        moveCollaboration(oldIndex, newIndex);
      }
      return;
    }

    // Handle unified links - simple reorder
    const activeIndex = linkFields.findIndex(field => `link-${field.id}` === activeId);
    const overIndex = linkFields.findIndex(field => `link-${field.id}` === overId);

    if (activeIndex !== -1 && overIndex !== -1) {
      moveLink(activeIndex, overIndex);
    }
  };


  // Form submission
  const onSubmit = async (data: ProfileData) => {
    setSaving(true);

    try {
      // Split unified links back to social_links and custom_links
      const socialLinks: SocialLink[] = [];
      const customLinks: CustomLink[] = [];

      (data.links || []).forEach((link, index) => {
        if (link.kind === 'social') {
          socialLinks.push({
            id: link.id,
            platform: link.platform || 'website',
            label: link.label || undefined,
            url: link.url,
            display_order: index
          });
        } else {
          customLinks.push({
            id: link.id,
            label: link.label || 'Link',
            icon: link.icon || 'Link',
            url: link.url,
            display_order: index
          });
        }
      });

      // Prepare the payload with display_order set correctly
      // Debug: Check form values
      const allFormValues = getValues();
      console.log('[Profile] All form values from getValues():', allFormValues);
      console.log('[Profile] contact_form_email from data param:', data.contact_form_email);
      console.log('[Profile] contact_form_email from getValues():', allFormValues.contact_form_email);
      console.log('[Profile] contact_form_email type:', typeof data.contact_form_email);
      console.log('[Profile] contact_form_email value check:', data.contact_form_email !== null && data.contact_form_email !== undefined);

      // Use getValues() as fallback if data doesn't have the value
      const contactFormEmail = data.contact_form_email !== undefined ? data.contact_form_email : (allFormValues.contact_form_email !== undefined ? allFormValues.contact_form_email : '');
      console.log('[Profile] Final contactFormEmail value:', contactFormEmail);

      const payload = {
        name: data.name || '',
        tagline: data.tagline || '',
        location: data.location || '',
        phone: data.phone || '',
        secondary_phone: data.secondary_phone || null,
        email: data.email || '',
        bio: data.bio || '',
        profile_image: data.profile_image || '',
        upi_id: data.upi_id || '', // Keep for backward compatibility
        upi_qr: data.upi_qr || '', // Keep for backward compatibility
        contact_form_title: (data.contact_form_title && data.contact_form_title.trim()) ? data.contact_form_title.trim() : null,
        contact_form_button_text: (data.contact_form_button_text && data.contact_form_button_text.trim()) ? data.contact_form_button_text.trim() : null,
        contact_form_enabled: data.contact_form_enabled ?? true,
        contact_form_email: contactFormEmail && contactFormEmail.toString().trim() ? contactFormEmail.toString().trim() : null,
        bg_color: bgColor || '#f5c6aa',
        bg_image: bgImage || null,
        card_color: cardColor || '#4a4a4a',
        text_color: (themeStyle === 'theme1' || themeStyle === 'theme2') ? 'black' : (textColor === 'black' ? 'black' : 'white'),
        icon_shape: iconShape || 'circle',
        banner_shape: bannerShape || 'circle',
        right_panel_color: rightPanelColor || '#ffffff',
        theme_style: themeStyle || 'theme1',
        social_links: socialLinks,
        custom_links: customLinks,
        collaborations: (data.collaborations || []).map((collab, index) => ({
          ...collab,
          display_order: index
        })),
        seo_title: data.seo_title || null,
        seo_description: data.seo_description || null,
        seo_keywords: data.seo_keywords || null,
      };

      // Send profile update as JSON
      console.log('[Profile] Sending payload:', JSON.stringify(payload, null, 2));
      const response = await api.post('/profiles/me', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        // Save UPI payments separately
        const upiPayments = data.upi_payments || [];

        // Get existing UPI payments from backend to compare
        const existingResponse = await api.get('/upi-payments');
        const existingPayments = existingResponse.data.success ? existingResponse.data.upi_payments || [] : [];
        const existingIds = new Set(existingPayments.map((p: UpiPayment) => p.id).filter(Boolean));

        // Process each UPI payment
        for (let i = 0; i < upiPayments.length; i++) {
          const upiPayment = upiPayments[i];

          if (!upiPayment.upi_id || !upiPayment.upi_id.trim()) {
            continue; // Skip empty UPI IDs
          }

          if (upiPayment.id && existingIds.has(upiPayment.id)) {
            // Update existing
            await api.put('/upi-payments', {
              id: upiPayment.id,
              name: upiPayment.name?.trim() || null,
              upi_id: upiPayment.upi_id.trim(),
              upi_qr: upiPayment.upi_qr || null,
              display_order: i
            });
          } else {
            // Create new
            await api.post('/upi-payments', {
              name: upiPayment.name?.trim() || null,
              upi_id: upiPayment.upi_id.trim(),
              upi_qr: upiPayment.upi_qr || null,
              display_order: i
            });
          }
        }

        // Reload UPI payments to get all IDs after saving
        try {
          const upiResponse = await api.get('/upi-payments');
          if (upiResponse.data.success && Array.isArray(upiResponse.data.upi_payments)) {
            // Update form with all saved UPI payments including their IDs
            const savedPayments = upiResponse.data.upi_payments;
            if (savedPayments.length > 0) {
              // Match saved payments with form entries by upi_id
              savedPayments.forEach((savedPayment: UpiPayment) => {
                const formIndex = upiPayments.findIndex(
                  (p: UpiPayment) => p.upi_id === savedPayment.upi_id
                );
                if (formIndex !== -1 && savedPayment.id) {
                  setValue(`upi_payments.${formIndex}.id` as const, savedPayment.id);
                  if (savedPayment.name) {
                    setValue(`upi_payments.${formIndex}.name` as const, savedPayment.name);
                  }
                  if (savedPayment.upi_qr) {
                    setValue(`upi_payments.${formIndex}.upi_qr` as const, savedPayment.upi_qr);
                  }
                }
              });
            }
          }
        } catch (upiError) {
          console.error('Failed to reload UPI payments:', upiError);
          // Don't fail the whole save if this fails
        }

        // Delete UPI payments that were removed
        const currentIds = new Set(upiPayments.map((p: UpiPayment) => p.id).filter(Boolean));
        for (const existing of existingPayments) {
          if (existing.id && !currentIds.has(existing.id)) {
            await api.delete(`/upi-payments/${existing.id}`);
          }
        }
        toast.success('Profile saved successfully!');
        // Refresh profile data to ensure form is updated with latest values
        await fetchProfile();
      }
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save profile';
      const errorDetails = error.response?.data?.debug || error.response?.data;
      console.error('Error details:', JSON.stringify(errorDetails, null, 2));
      console.error('Full error response:', JSON.stringify(error.response?.data, null, 2));
      console.error('Error status:', error.response?.status);
      toast.error(errorMessage + (errorDetails ? ` (Check console for details)` : ''));
    } finally {
      setSaving(false);
    }
  };

  const downloadQRCode = (format: 'png' | 'svg') => {
    if (format === 'png' && qrCodeUrl) {
      const link = document.createElement('a');
      link.download = 'profile-qr-code.png';
      link.href = qrCodeUrl;
      link.click();
    } else if (format === 'svg' && qrCodeSvg) {
      const svgElement = document.getElementById('qr-code-svg');
      if (svgElement) {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          const pngFile = canvas.toDataURL('image/png');

          const link = document.createElement('a');
          link.download = 'profile-qr-code.png';
          link.href = pngFile;
          link.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }
    }
  };

  const handleCollaborationLogoUpload = async (file: File, index: number) => {
    // Validate file size (1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Logo size must be less than 1MB');
      return;
    }

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await api.post('/upload/collaboration-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setValue(`collaborations.${index}.logo` as const, response.data.logo_url);
        toast.success('Logo uploaded successfully');
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleUpiQRUpload = async (file: File, index: number) => {
    // Validate file size (1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error('QR code size must be less than 1MB');
      return;
    }

    // Show immediate preview using createObjectURL while upload is in progress
    const previewUrl = URL.createObjectURL(file);
    setValue(`upi_payments.${index}.upi_qr` as const, previewUrl);

    const formData = new FormData();
    formData.append('qr', file);

    try {
      const response = await api.post('/upload/upi-qr', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        URL.revokeObjectURL(previewUrl);
        setValue(`upi_payments.${index}.upi_qr` as const, response.data.qr_url);
        toast.success('UPI QR code uploaded successfully');
      }
    } catch (error) {
      console.error('Failed to upload UPI QR code:', error);
      toast.error('Failed to upload UPI QR code');
      setValue(`upi_payments.${index}.upi_qr` as const, '');
      URL.revokeObjectURL(previewUrl);
    }
  };

  // Add new UPI payment
  const addUpiPayment = () => {
    appendUpiPayment({
      name: '',
      upi_id: '',
      upi_qr: '',
      display_order: upiPaymentFields.length
    });
  };

  // Remove UPI payment
  const removeUpiPaymentEntry = async (index: number) => {
    // Get the actual form value for the ID (not the React Hook Form field ID)
    const dbId = watch(`upi_payments.${index}.id`);
    const upiPayment = upiPaymentFields[index];

    // Check if it has a database ID
    const paymentId = dbId || (upiPayment as any)?.id;

    // If it has an ID, delete it from backend
    if (paymentId && typeof paymentId === 'number' && paymentId > 0) {
      try {
        // Use path parameter for DELETE request
        console.log('Deleting UPI ID with ID:', paymentId);
        const response = await api.delete(`/upi-payments/${paymentId}`);
        if (response.data.success) {
          removeUpiPayment(index);
          toast.success('UPI ID removed');
        } else {
          throw new Error(response.data.message || 'Failed to delete');
        }
      } catch (error: any) {
        console.error('Failed to delete UPI ID:', error);
        console.error('Error details:', {
          paymentId: paymentId,
          fieldData: upiPayment,
          response: error.response?.data,
          status: error.response?.status
        });
        const errorMessage = error.response?.data?.message || error.message || 'Failed to delete UPI ID';
        toast.error(errorMessage);
        // Don't remove from form if backend delete failed
        return;
      }
    } else {
      // No ID means it's a new entry that hasn't been saved yet - just remove from form
      console.log('Removing unsaved UPI ID entry (no ID)', { paymentId, upiPayment });
      removeUpiPayment(index);
      toast.success('UPI ID removed');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || hasError) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <CloseIcon className="h-12 w-12 mx-auto mb-2" />
              <h2 className="text-xl font-semibold mb-2">Error Loading Profile</h2>
              <p className="text-secondary-600 mb-4">{error || 'An error occurred while loading your profile'}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                setHasError(false);
                setLoading(true);
                fetchProfile();
              }}
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Always render something - even if there's an issue, show the form
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Profile Settings</h1>
        <p className="text-secondary-600">
          Customize your public profile and manage your links
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Section Menu - shown when no section is selected */}
            {activeSection === null && (() => {
              const vals = watch();
              const getSectionStatus = (section: string) => {
                switch(section) {
                  case 'profile':
                    const req = [vals.name, vals.email, vals.phone];
                    const filled = req.filter(Boolean).length;
                    if (filled === req.length && profileImageUrl) return 'completed';
                    if (filled > 0 || profileImageUrl) return 'partial';
                    return 'not_started';
                  case 'links':
                    return (vals.links && vals.links.length > 0) ? 'completed' : 'not_started';
                  case 'collaboration':
                    return (vals.collaborations && vals.collaborations.length > 0) ? 'completed' : 'not_started';
                  case 'upi':
                    return (vals.upi_payments && vals.upi_payments.length > 0) || vals.upi_id ? 'completed' : 'not_started';
                  case 'seo':
                    if (vals.seo_title && vals.seo_description) return 'completed';
                    if (vals.seo_title || vals.seo_description) return 'partial';
                    return 'not_started';
                  case 'appearance':
                    return 'completed';
                  case 'contact':
                    if (vals.contact_form_enabled && vals.contact_form_email) return 'completed';
                    if (vals.contact_form_enabled) return 'partial';
                    return 'not_started';
                  default:
                    return 'not_started';
                }
              };

              const SECTIONS = [
                { id: 'profile', title: 'Profile', desc: 'Profile Image, Basic Information', icon: User, color: 'text-blue-600', bg: 'bg-blue-100' },
                { id: 'links', title: 'Links', desc: 'Social Links, Custom Links', icon: LinkIcon, color: 'text-indigo-600', bg: 'bg-indigo-100' },
                { id: 'collaboration', title: 'Collaboration', desc: 'Proud Member Of', icon: Users, color: 'text-purple-600', bg: 'bg-purple-100' },
                { id: 'upi', title: 'UPI Settings', desc: 'UPI ID, Payment Options', icon: CreditCard, color: 'text-pink-600', bg: 'bg-pink-100' },
                { id: 'seo', title: 'SEO & Search', desc: 'SEO Settings, Search Preview', icon: Search, color: 'text-orange-600', bg: 'bg-orange-100' },
                { id: 'appearance', title: 'Appearance Settings', desc: 'Theme, Colors, QR Code', icon: Palette, color: 'text-green-600', bg: 'bg-green-100' },
                { id: 'contact', title: 'Contact Form', desc: 'Contact Form Settings', icon: Mail, color: 'text-teal-600', bg: 'bg-teal-100' },
              ];

              return (
                <div className="space-y-4">
                  {SECTIONS.map(sec => {
                    const status = getSectionStatus(sec.id);
                    return (
                      <div key={sec.id} onClick={() => setActiveSection(sec.id)} className="bg-white rounded-2xl border border-gray-100 p-5 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all group">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl ${sec.bg} flex items-center justify-center ${sec.color} group-hover:scale-105 transition-transform`}>
                              <sec.icon className="h-6 w-6" />
                            </div>
                            <div>
                              <h2 className="text-lg font-bold text-gray-900">{sec.title}</h2>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">{sec.desc}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {status === 'completed' && (
                              <div className="flex items-center gap-1.5" title="Completed">
                                <span className="hidden sm:inline text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Completed</span>
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 shrink-0">
                                  <Check className="h-3.5 w-3.5 stroke-[3]" />
                                </div>
                              </div>
                            )}
                            {status === 'partial' && (
                              <div className="flex items-center gap-1.5" title="In progress">
                                <span className="hidden sm:inline text-[10px] font-bold text-amber-500 uppercase tracking-wider">In Progress</span>
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 border-2 border-amber-400 shrink-0">
                                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                                </div>
                              </div>
                            )}
                            {status === 'not_started' && (
                              <div className="flex items-center gap-1.5" title="Not Started">
                                <span className="hidden sm:inline text-[10px] font-bold text-gray-400 uppercase tracking-wider">Not Started</span>
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-50 border-2 border-gray-300 shrink-0">
                                  <div className="w-2 h-2 rounded-full bg-gray-300" />
                                </div>
                              </div>
                            )}
                            <ChevronDown className="h-5 w-5 text-gray-300 -rotate-90 group-hover:text-gray-500 transition-colors shrink-0" />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              );
            })()}

            {/* Profile Section - Profile Image + Basic Information */}
            {activeSection === 'profile' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

                <div className="card">
                  <h2 className="text-xl font-semibold text-secondary-900 mb-6">Profile Image</h2>

              <div className="flex items-start space-x-6">
                {/* Image Preview */}
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 bg-secondary-100 rounded-full flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="w-full h-full object-cover bg-white"
                      />
                    ) : (
                      <Upload className="h-8 w-8 text-secondary-400" />
                    )}
                  </div>
                  {/* Delete button — only visible when a photo is set */}
                  {profileImageUrl && (
                    <button
                      type="button"
                      title="Remove profile photo"
                      onClick={() => {
                        setProfileImageUrl('');
                        setValue('profile_image', '');
                        setProfileImage(null);
                        toast.success('Profile photo removed. Click Save to apply.');
                      }}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors z-10"
                    >
                      <span className="text-xs font-bold leading-none">×</span>
                    </button>
                  )}
                </div>

                {/* Upload Area */}
                <div className="flex-1">
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragActive
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-secondary-300 hover:border-primary-400 hover:bg-secondary-50'
                      }`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="h-8 w-8 text-secondary-400 mx-auto mb-2" />
                    <p className="text-sm text-secondary-600 mb-1">
                      {isDragActive ? 'Drop the image here' : 'Drag & drop or click to upload'}
                    </p>
                    <p className="text-xs text-secondary-500">
                      JPG, JPEG, PNG, WEBP up to 2MB
                    </p>
                  </div>
                </div>
              </div>
              {/* Helper text shown when image is set */}
              {profileImageUrl && (
                <p className="mt-3 text-xs text-secondary-400">
                  Click the <span className="font-semibold text-red-400">×</span> button on your photo to remove it, then hit <span className="font-semibold">Save</span> to apply.
                </p>
              )}
            </div>
              </>
            )}

            {/* Appearance Section - shown when appearance is selected */}
            {activeSection === 'appearance' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

                <div className="card">
                  <h2 className="text-xl font-semibold text-secondary-900 mb-6">Appearance Settings</h2>
                  <p className="text-sm text-secondary-500 mb-6">Customize how your public profile looks</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Background Color - Hidden for Theme 3, Theme 1, and Theme 2 */}
                {themeStyle !== 'theme3' && themeStyle !== 'theme1' && themeStyle !== 'theme2' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Background Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="w-10 h-10 rounded-lg border border-secondary-300 cursor-pointer p-0.5"
                      />
                      <input
                        type="text"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="input-field flex-1"
                        placeholder="#f5c6aa"
                        maxLength={20}
                      />
                    </div>
                  </div>
                )}

                {/* Card Colors - Hidden for Theme 3, Theme 1, and Theme 2 */}
                {themeStyle !== 'theme3' && themeStyle !== 'theme1' && themeStyle !== 'theme2' && (
                  <div className={`grid grid-cols-1 ${themeStyle === 'theme1' ? 'md:grid-cols-2' : ''} gap-6`}>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        {themeStyle === 'theme1' ? 'Left Panel Color' : 'Card Color'}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={cardColor}
                          onChange={(e) => setCardColor(e.target.value)}
                          className="w-10 h-10 rounded-lg border border-secondary-300 cursor-pointer p-0.5"
                        />
                        <input
                          type="text"
                          value={cardColor}
                          onChange={(e) => setCardColor(e.target.value)}
                          className="input-field flex-1"
                          placeholder="#4a4a4a"
                          maxLength={20}
                        />
                      </div>
                    </div>
                    {themeStyle === 'theme1' && (
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Right Panel Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={rightPanelColor}
                            onChange={(e) => setRightPanelColor(e.target.value)}
                            className="w-10 h-10 rounded-lg border border-secondary-300 cursor-pointer p-0.5"
                          />
                          <input
                            type="text"
                            value={rightPanelColor}
                            onChange={(e) => setRightPanelColor(e.target.value)}
                            className="input-field flex-1"
                            placeholder="#ffffff"
                            maxLength={20}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Split Layout Color Themes - Only for Theme 1 */}
                {themeStyle === 'theme1' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-3">
                      Color Theme
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {SPLIT_LAYOUT_THEMES.map((theme) => {
                        // Case-insensitive comparison for hex codes
                        const isSelected =
                          bgColor?.toLowerCase() === theme.bg.toLowerCase() &&
                          cardColor?.toLowerCase() === theme.left.toLowerCase() &&
                          rightPanelColor?.toLowerCase() === theme.right.toLowerCase();

                        return (
                          <button
                            key={theme.id}
                            type="button"
                            onClick={() => {
                              setBgColor(theme.bg);
                              setCardColor(theme.left);
                              setRightPanelColor(theme.right);
                            }}
                            className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-all ${isSelected
                              ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600/20'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50 text-gray-700'
                              }`}
                          >
                            <div className="w-full flex h-16 rounded-lg overflow-hidden border border-gray-200 mb-2 shadow-sm" style={{ backgroundColor: theme.bg }}>
                              {/* Background color of theme is applied to card. Left and Right act as overlays. */}
                              <div className="w-1/2 h-full" style={{ backgroundColor: theme.left }} />
                              <div className="w-1/2 h-full relative" style={{ backgroundColor: theme.right }}>
                                <div className="absolute inset-y-0 left-0 w-[2px] bg-black opacity-30" />
                              </div>
                            </div>
                            <span className={`text-xs text-center font-medium ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
                              {theme.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Simple Card Color Themes - Only for Theme 2 */}
                {themeStyle === 'theme2' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-3">
                      Color Theme
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {SIMPLE_CARD_THEMES.map((theme) => {
                        const isSelected =
                          bgColor?.toLowerCase() === theme.bg.toLowerCase() &&
                          cardColor?.toLowerCase() === theme.card.toLowerCase();

                        return (
                          <button
                            key={theme.id}
                            type="button"
                            onClick={() => {
                              setBgColor(theme.bg);
                              setCardColor(theme.card);
                            }}
                            className={`relative flex items-center justify-center p-3 rounded-xl border-2 transition-all ${isSelected
                              ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600/20'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50 text-gray-700'
                              }`}
                          >
                            <div className="w-full flex flex-col items-center">
                              <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-200 shadow-sm flex items-center justify-center mb-2" style={{ backgroundColor: theme.bg }}>
                                <div className="w-12 h-10 rounded-lg shadow-sm" style={{ backgroundColor: theme.card }} />
                              </div>
                              <span className={`text-xs text-center font-medium ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
                                {theme.name}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Background Image - Hidden for Theme 3 */}
                {themeStyle !== 'theme3' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Background Image <span className="text-secondary-400 font-normal">(optional, overrides background color)</span>
                    </label>
                    <div className="flex items-center gap-4">
                      {bgImage && (
                        <div className="relative w-24 h-16 rounded-lg overflow-hidden border border-secondary-200 flex-shrink-0">
                          <img
                            src={bgImage.startsWith('http') ? bgImage : `${API_CONFIG.BASE_URL}${bgImage}`}
                            alt="Background preview"
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => setBgImage('')}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                          >
                            <CloseIcon className="h-3 w-3 text-white" />
                          </button>
                        </div>
                      )}
                      <label className={`inline-flex items-center gap-2 px-4 py-2 border border-secondary-300 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors text-sm ${uploadingBg ? 'opacity-50 pointer-events-none' : ''}`}>
                        <Upload className="h-4 w-4 text-secondary-500" />
                        {uploadingBg ? 'Uploading...' : 'Upload Image'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error('Image must be under 2MB');
                              return;
                            }
                            setUploadingBg(true);
                            try {
                              const formData = new FormData();
                              formData.append('image', file);
                              const res = await api.post('/upload/background-image', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' },
                              });
                              if (res.data.success) {
                                setBgImage(res.data.image_url);
                                toast.success('Background image uploaded');
                              } else {
                                toast.error(res.data.message || 'Upload failed');
                              }
                            } catch (err: any) {
                              toast.error(err.response?.data?.message || 'Upload failed');
                            } finally {
                              setUploadingBg(false);
                              e.target.value = '';
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                )}

                {/* Live Preview */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Public Profile Preview</label>
                  <div
                    className="relative w-full rounded-2xl overflow-hidden border border-secondary-200 min-h-[360px] md:min-h-[420px]"
                    style={{
                      backgroundColor: themeStyle === 'theme3' ? '#f3f4f6' : bgColor,
                      backgroundImage: themeStyle === 'theme3' ? undefined : (previewBgImage ? `url(${previewBgImage})` : undefined),
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="relative z-10 px-4 py-8 md:px-6 md:py-10 flex items-center justify-center">
                      {themeStyle === 'theme2' ? (
                        <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl bg-white/95">
                          <div className="px-4 py-3" style={{ backgroundColor: cardColor }}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white/80 text-gray-700">
                                <QrCodeIcon className="h-3 w-3" />
                                <span>View QR</span>
                              </div>
                              {profileImageUrl ? (
                                <img src={profileImageUrl} alt="Profile preview" className="w-12 h-12 rounded-full object-cover border-2 border-white/70 bg-white" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                                  <span className="text-base font-bold text-white/70">{previewName.charAt(0).toUpperCase()}</span>
                                </div>
                              )}
                              <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white/80 text-gray-700">
                                <Share2 className="h-3 w-3" />
                                <span>Share</span>
                              </div>
                            </div>
                            <h3 className="text-lg font-bold text-center mb-1" style={{ color: previewTextPrimary }}>{previewName}</h3>
                            <p className="text-center text-xs mb-2" style={{ color: previewTextSoft }}>{previewTagline}</p>
                            <div className="flex items-center justify-center gap-1 text-xs" style={{ color: previewTextMuted }}>
                              <MapPin className="h-3 w-3" />
                              <span>{previewLocation}</span>
                            </div>
                          </div>
                          <div className="p-4 bg-white">
                            <p className="text-xs text-gray-600 text-center mb-3 line-clamp-2">{previewBio}</p>
                            <div className="space-y-2 mb-3">
                              {(previewLinks.length > 0 ? previewLinks : [{ id: 'placeholder', label: 'Sample Link' }]).slice(0, 3).map((link: any, idx: number) => (
                                <div key={`theme2-preview-link-${link.id ?? idx}`} className="w-full px-3 py-2 rounded-lg text-white text-sm bg-gradient-to-r from-fuchsia-500 to-blue-600">
                                  {link?.label || link?.platform || 'Link'}
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center justify-center gap-2 mb-2">
                              <span className="px-2 py-1 rounded-full bg-gray-100 text-[11px] text-gray-700">Mail</span>
                              <span className="px-2 py-1 rounded-full bg-gray-100 text-[11px] text-gray-700">Save Contact</span>
                            </div>
                            <div className="w-full text-center px-3 py-2 rounded-lg text-white text-sm font-semibold mb-2 bg-gradient-to-r from-green-600 to-green-500">UPI ID</div>
                            <div className="w-full text-center px-3 py-2 rounded-lg text-white text-sm font-semibold bg-gradient-to-r from-blue-600 to-blue-500">Contact Me</div>
                          </div>
                        </div>
                      ) : themeStyle === 'theme3' ? (
                        <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-100">
                          <div className="p-5 text-center">
                            {profileImageUrl ? (
                              <img src={profileImageUrl} alt="Profile preview" className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-2 border-gray-200 bg-white" />
                            ) : (
                              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl font-bold text-gray-500">{previewName.charAt(0).toUpperCase()}</span>
                              </div>
                            )}
                            <h3 className="text-xl font-bold text-gray-800 mb-1">{previewName}</h3>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{previewBio}</p>
                            <div className="space-y-2 mb-3">
                              {(previewLinks.length > 0 ? previewLinks : [{ id: 'p1', label: 'GitHub' }, { id: 'p2', label: 'Instagram' }]).slice(0, 3).map((link: any, idx: number) => (
                                <div key={`theme3-preview-${link.id ?? idx}`} className="w-full px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-[#e6f7f5] flex items-center justify-between">
                                  <span>{link?.label || link?.platform || 'Link'}</span>
                                  <LinkIcon className="h-4 w-4 text-gray-500" />
                                </div>
                              ))}
                            </div>
                            {hasFeature('collaborations') && previewCollaborations.length > 0 && (
                              <div className="mb-3">
                                <p className="text-center text-xs font-semibold text-gray-600 mb-2">Proud Member of</p>
                                <div className="space-y-1.5">
                                  {previewCollaborations.map((collab: any, idx: number) => (
                                    <div key={`theme3-preview-collab-${collab.id ?? idx}`} className="flex items-center gap-2 p-2 rounded-xl bg-gray-50">
                                      {collab.logo && (
                                        <img
                                          src={convertToAbsoluteImageUrl(collab.logo)}
                                          alt={collab.name}
                                          className="w-6 h-6 rounded-md object-cover flex-shrink-0"
                                        />
                                      )}
                                      <span className="text-xs text-gray-700 truncate">{collab.name}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="w-full py-2.5 rounded-xl text-sm font-semibold text-white mb-2 bg-green-600">UPI ID</div>
                            <div className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600">Contact Me</div>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-1/2 relative flex items-center justify-center p-5 md:p-6 transition-colors duration-300 border-b-2 md:border-b-0 md:border-r-2 border-black" style={{ backgroundColor: cardColor }}>
                              {profileImageUrl ? (
                                <img
                                  src={profileImageUrl}
                                  alt="Profile preview"
                                  className={`w-28 h-28 md:w-36 md:h-36 object-cover shadow-xl ${bannerShape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`}
                                />
                              ) : (
                                <div className={`w-28 h-28 md:w-36 md:h-36 bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30 shadow-xl ${bannerShape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`}>
                                  <span className="text-4xl font-bold text-white/50">
                                    {previewName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="md:w-1/2 px-5 py-5 md:px-7 md:py-6 flex flex-col justify-center" style={{ backgroundColor: rightPanelColor }}>
                              <p className="text-[10px] md:text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: previewTextSoft }}>
                                {previewTagline}
                              </p>
                              <h3 className="text-xl md:text-2xl font-bold mb-2.5" style={{ color: previewTextPrimary }}>
                                {previewName}
                              </h3>
                              <div className="w-10 h-0.5 rounded-full mb-3" style={{ backgroundColor: bgColor }} />
                              <p className="text-xs md:text-sm leading-relaxed mb-3 line-clamp-3" style={{ color: previewTextMuted }}>
                                {previewBio}
                              </p>
                              <div className="inline-flex items-center gap-1.5 text-xs mb-4" style={{ color: previewTextSoft }}>
                                <MapPin className="h-3 w-3" />
                                <span>{previewLocation}</span>
                              </div>
                              <div className="flex items-center gap-2 mb-4">
                                {[Phone, MessageCircle, Mail, User, Globe].map((PreviewIcon, idx) => (
                                  <div
                                    key={`preview-action-${idx}`}
                                    className={`w-8 h-8 ${previewIconShapeClass} flex items-center justify-center`}
                                    style={previewIconStyle}
                                  >
                                    <PreviewIcon className="h-3.5 w-3.5" style={{ color: previewTextMuted }} />
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-black/5 mt-auto">
                                {(previewLinks.length > 0 ? previewLinks : [{ id: 'placeholder' }]).map((link: any, idx: number) => (
                                  <div
                                    key={`preview-link-${link.id ?? idx}`}
                                    className={`w-8 h-8 ${previewIconShapeClass} flex items-center justify-center`}
                                    style={previewIconStyle}
                                    title={link?.label || link?.platform || 'Link'}
                                  >
                                    <LinkIcon className="h-3.5 w-3.5" style={{ color: previewTextMuted }} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Text Color - Hidden for Theme 3, Theme 1, and Theme 2 */}
                {themeStyle !== 'theme3' && themeStyle !== 'theme1' && themeStyle !== 'theme2' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Text Color
                    </label>
                    <p className="text-xs text-secondary-500 mb-3">
                      Choose a text color for your public profile card content.
                    </p>
                    <div className="flex items-center gap-4">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="text_color_option"
                          checked={textColor === 'white'}
                          onChange={() => setTextColor('white')}
                          className="h-4 w-4 text-primary-600 border-secondary-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">White</span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="text_color_option"
                          checked={textColor === 'black'}
                          onChange={() => setTextColor('black')}
                          className="h-4 w-4 text-primary-600 border-secondary-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">Black</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Icon Shape - Only show for Theme 1 */}
                {themeStyle === 'theme1' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Icon Shape
                    </label>
                    <p className="text-xs text-secondary-500 mb-3">
                      Choose how action and social icons look on your public profile.
                    </p>
                    <select
                      value={iconShape}
                      onChange={(e) => setIconShape(e.target.value)}
                      className="input-field mb-4"
                    >
                      {themeStyle === 'theme1' ? (
                        <>
                          <option value="circle">Circle</option>
                          <option value="rounded-square">Square</option>
                          <option value="glass-circle">Glass Circle</option>
                        </>
                      ) : (
                        <>
                          <option value="circle">Circle</option>
                          <option value="rounded-square">Rounded Square</option>
                          <option value="squircle">Squircle</option>
                          <option value="glass-circle">Glass Circle</option>
                          <option value="gradient-card">Gradient Card</option>
                        </>
                      )}
                    </select>

                    {/* Banner Shape - Only for Theme 1 */}
                    <div className="pt-2 border-t border-gray-100 mt-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Banner Shape
                      </label>
                      <p className="text-xs text-secondary-500 mb-3">
                        Choose the shape of your profile picture.
                      </p>
                      <select
                        value={bannerShape}
                        onChange={(e) => setBannerShape(e.target.value)}
                        className="input-field"
                      >
                        <option value="circle">Circle Banner</option>
                        <option value="rounded-square">Square Banner</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Theme Style */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Theme
                  </label>
                  <p className="text-xs text-secondary-500 mb-3">
                    Choose which public profile layout to show.
                  </p>
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <select
                        value={themeStyle}
                        onChange={(e) => setThemeStyle(e.target.value)}
                        className="input-field w-full"
                      >
                        <option value="theme2">Simple Card (Default)</option>
                        <option value="theme1">Split Layout</option>
                        <option value="theme3">Professional Card</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-medium shadow-sm mb-[1px]"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-secondary-900">QR Code</h2>
                <button
                  type="button"
                  onClick={() => setQrCodeStyle({ ...qrCodeStyle, fgColor: qrCodeStyle.fgColor === '#000000' ? '#2563eb' : '#000000' })}
                  className="btn-secondary text-xs"
                >
                  <Palette className="h-3 w-3 mr-1" />
                  Style
                </button>
              </div>

              <div className="text-center">
                <div className="w-48 h-48 bg-white border-2 border-secondary-200 rounded-lg flex items-center justify-center mx-auto mb-4">
                  {qrCodeSvg ? (
                    <QRCodeSVG
                      id="qr-code-svg"
                      value={qrCodeSvg}
                      size={180}
                      fgColor={qrCodeStyle.fgColor}
                      bgColor={qrCodeStyle.bgColor}
                    />
                  ) : (
                    <QrCodeIcon className="h-12 w-12 text-secondary-400" />
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => downloadQRCode('png')}
                    className="btn-primary flex-1 text-sm text-white hover:text-white"
                    disabled={!qrCodeUrl}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    PNG
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadQRCode('svg')}
                    className="btn-secondary flex-1 text-sm"
                    disabled={!qrCodeSvg}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    SVG
                  </button>
                </div>

                {/* Sharable URL */}
                {profileSlug && (
                  <div className="mt-4 p-3 bg-secondary-50 rounded-lg">
                    <p className="text-xs text-secondary-600 mb-2 font-medium">Your Profile URL:</p>
                    <div className="flex items-center space-x-2">
                      <a
                        href={primaryProfileUrl || userProfileUrl || legacyProfileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-700 font-mono break-all flex-1"
                        data-user-url={userProfileUrl}
                        data-legacy-url={legacyProfileUrl}
                      >
                        {formatDisplayUrl(primaryProfileUrl || userProfileUrl || legacyProfileUrl, profileSlug)}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(primaryProfileUrl || userProfileUrl || legacyProfileUrl);
                          toast.success('URL copied to clipboard!');
                        }}
                        className="text-secondary-500 hover:text-secondary-700 p-1"
                        title="Copy URL"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={saving}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setActiveSection(null)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
              >
                <ChevronDown className="h-4 w-4 rotate-90" />
                Back
              </button>
            </div>
              </>
            )}

            {/* Basic Information - Only show in Profile section */}
            {activeSection === 'profile' && (
            <div className="card">
              <h2 className="text-xl font-semibold text-secondary-900 mb-6">Basic Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-secondary-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    {...register('name', { required: 'Name is required', maxLength: 100 })}
                    type="text"
                    id="name"
                    className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                {/* Tagline */}
                <div className="md:col-span-2">
                  <label htmlFor="tagline" className="block text-sm font-medium text-secondary-700 mb-2">
                    Professional Tagline
                  </label>
                  <input
                    {...register('tagline', {
                      maxLength: {
                        value: 60,
                        message: 'Professional Tagline must not exceed 60 characters'
                      }
                    })}
                    type="text"
                    id="tagline"
                    maxLength={60}
                    className={`input-field ${errors.tagline ? 'border-red-300' : ''}`}
                    placeholder="e.g., Digital Creator & Entrepreneur"
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData('text');
                      const currentValue = watch('tagline') || '';
                      const newValue = (currentValue + pastedText).slice(0, 60);
                      if (newValue.length < (currentValue + pastedText).length) {
                        e.preventDefault();
                        setValue('tagline', newValue);
                      }
                    }}
                  />
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-xs text-secondary-500">
                      {watch('tagline')?.length || 0} / 60 characters
                    </p>
                    {errors.tagline && (
                      <p className="text-xs text-red-600">{errors.tagline.message}</p>
                    )}
                  </div>
                </div>

                {/* Phone Number */}
                <div className="md:col-span-2">
                  <label htmlFor="phone" className="block text-sm font-medium text-secondary-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    {...register('phone', {
                      pattern: {
                        value: /^[\+]?[0-9\s\-\(\)]+$/,
                        message: 'Please enter a valid phone number'
                      },
                      maxLength: 50
                    })}
                    type="tel"
                    id="phone"
                    className={`input-field ${errors.phone ? 'border-red-300' : ''}`}
                    placeholder="Enter your phone number"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                {/* Secondary Phone Number */}
                <div className="md:col-span-2">
                  <label htmlFor="secondary_phone" className="block text-sm font-medium text-secondary-700 mb-2">
                    Secondary Phone Number
                  </label>
                  <input
                    {...register('secondary_phone', {
                      pattern: {
                        value: /^[\+]?[0-9\s\-\(\)]+$/,
                        message: 'Please enter a valid phone number'
                      },
                      maxLength: 50
                    })}
                    type="tel"
                    id="secondary_phone"
                    className={`input-field ${errors.secondary_phone ? 'border-red-300' : ''}`}
                    placeholder="Enter your secondary phone number (optional)"
                  />
                  {errors.secondary_phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.secondary_phone.message}</p>
                  )}
                </div>

                {/* Email */}
                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                    Email
                  </label>
                  <input
                    {...register('email', {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    type="email"
                    id="email"
                    className={`input-field ${errors.email ? 'border-red-300' : ''}`}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-secondary-700 mb-2">
                    Location
                  </label>
                  <input
                    {...register('location', { maxLength: 100 })}
                    type="text"
                    id="location"
                    className="input-field"
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>

                {/* Bio */}
                <div className="md:col-span-2">
                  <label htmlFor="bio" className="block text-sm font-medium text-secondary-700 mb-2">
                    Bio
                  </label>
                  <textarea
                    {...register('bio', {
                      maxLength: {
                        value: 150,
                        message: 'Bio must not exceed 150 characters'
                      }
                    })}
                    id="bio"
                    rows={4}
                    maxLength={150}
                    className={`input-field ${errors.bio ? 'border-red-300' : ''}`}
                    placeholder="Tell people about yourself..."
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData('text');
                      const currentValue = watch('bio') || '';
                      const newValue = (currentValue + pastedText).slice(0, 150);
                      if (newValue.length < (currentValue + pastedText).length) {
                        e.preventDefault();
                        setValue('bio', newValue);
                      }
                    }}
                  />
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-xs text-secondary-500">
                      {watch('bio')?.length || 0} / 150 characters
                    </p>
                    {errors.bio && (
                      <p className="text-xs text-red-600">{errors.bio.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
                >
                  <ChevronDown className="h-4 w-4 rotate-90" />
                  Back
                </button>
              </div>
            </div>
            )}

            {/* Links Section - Unified */}
            {activeSection === 'links' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

              <div className="card">
                {/* Fixed Social Links Header */}
                <div className="mb-6 pb-4 border-b border-secondary-200">
                  <h2 className="text-xl font-semibold text-secondary-900 mb-4">Social Links</h2>
                  <div className="flex flex-wrap gap-2">
                    {SOCIAL_PLATFORMS.map((platform) => (
                      <button
                        key={platform.key}
                        type="button"
                        onClick={() => addSocialLink(platform.key)}
                        className="flex items-center px-3 py-1 text-xs bg-secondary-100 hover:bg-secondary-200 rounded-full transition-colors"
                      >
                        <platform.icon className="h-3 w-3 mr-1" />
                        {platform.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fixed Custom Links Header */}
                <div className="mb-6 pb-4 border-b border-secondary-200">
                  <h2 className="text-xl font-semibold text-secondary-900 mb-4">Custom Links</h2>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAppSelector(true)}
                      className="flex items-center px-3 py-1 text-xs bg-secondary-100 hover:bg-secondary-200 rounded-full transition-colors"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Select App
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomAppSelector(true)}
                      className="flex items-center px-3 py-1 text-xs bg-secondary-100 hover:bg-secondary-200 rounded-full transition-colors"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Custom App
                    </button>
                  </div>
                </div>

                {/* Unified Sortable List */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={linkFields.map(field => `link-${field.id}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {linkFields.map((field, index) => {
                        const link = field as UnifiedLink;
                        return (
                          <SortableUnifiedLink
                            key={field.id}
                            id={`link-${field.id}`}
                            item={link}
                            index={index}
                            register={register}
                            watch={watch}
                            removeLink={removeLink}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>

                {/* Save Button */}
                <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveSection(null)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
                  >
                    <ChevronDown className="h-4 w-4 rotate-90" />
                    Back
                  </button>
                </div>
              </div>
              </>
            )}

            {/* Collaborations */}
            {activeSection === 'collaboration' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

            <div className="card">
              <div className="flex flex-wrap items-center gap-2 mb-6 md:justify-between">
                <h2 className="text-lg md:text-xl font-semibold text-secondary-900 whitespace-nowrap">Proud Member of</h2>
                <button
                  type="button"
                  onClick={addCollaboration}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap max-w-full text-sm md:text-base"
                >
                  Add Collaboration
                  <span className="text-lg leading-none">+</span>
                </button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={collaborationFields.map(field => `collaboration-${field.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {collaborationFields.map((field, index) => (
                      <SortableCollaboration
                        key={field.id}
                        id={`collaboration-${field.id}`}
                        index={index}
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        removeCollaboration={removeCollaboration}
                        handleCollaborationLogoUpload={handleCollaborationLogoUpload}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
                >
                  <ChevronDown className="h-4 w-4 rotate-90" />
                  Back
                </button>
              </div>
            </div>
              </>
            )}

            {/* UPI Payment Section - Multiple UPI IDs */}
            {activeSection === 'upi' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-secondary-900">UPI ID Settings</h2>
                <button
                  type="button"
                  onClick={addUpiPayment}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add UPI ID
                </button>
              </div>

                {upiPaymentFields.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-secondary-300 rounded-lg bg-secondary-50">
                    <p className="text-secondary-600 mb-2">No UPI IDs added yet</p>
                    <p className="text-sm text-secondary-500">Click "Add UPI ID" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upiPaymentFields.map((field, index) => {
                      const upiQrValue = watch(`upi_payments.${index}.upi_qr`);
                      const upiQrUrl = upiQrValue ? convertToAbsoluteImageUrl(upiQrValue) : null;

                      const upiNameValue = watch(`upi_payments.${index}.name`);
                      const displayName = upiNameValue?.trim() || `UPI Payment #${index + 1}`;

                      return (
                        <div key={field.id} className="border border-secondary-200 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="text-sm font-medium text-secondary-700">
                              {displayName}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeUpiPaymentEntry(index)}
                              className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span>Remove</span>
                            </button>
                          </div>

                          {/* UPI Payment Name */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              UPI ID Name (optional)
                            </label>
                            <input
                              {...register(`upi_payments.${index}.name` as const)}
                              type="text"
                              className="input-field"
                              placeholder="e.g., Personal UPI, Business Account, Paytm"
                            />
                            <p className="mt-1 text-xs text-secondary-500">
                              Give this payment method a custom name for easy identification
                            </p>
                          </div>

                          {/* UPI ID */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              UPI ID
                            </label>
                            <input
                              {...register(`upi_payments.${index}.upi_id` as const, {
                                pattern: {
                                  value: /^[a-zA-Z0-9._-]+@[a-zA-Z]+$/,
                                  message: 'Invalid UPI ID format. Expected: user@bank'
                                }
                              })}
                              type="text"
                              className={`input-field ${errors.upi_payments?.[index]?.upi_id ? 'border-red-300' : ''}`}
                              placeholder="yourname@bank"
                            />
                            {errors.upi_payments?.[index]?.upi_id && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.upi_payments[index]?.upi_id?.message}
                              </p>
                            )}
                            <p className="mt-1 text-xs text-secondary-500">
                              Example: john.doe@paytm or user123@ybl
                            </p>
                          </div>

                          {/* UPI QR Code */}
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              UPI QR Code
                            </label>
                            {upiQrUrl ? (
                              <div className="mb-4">
                                <img
                                  src={upiQrUrl}
                                  alt="UPI QR Code"
                                  className="w-32 h-32 border-2 border-secondary-200 rounded-lg object-contain bg-white"
                                />
                              </div>
                            ) : (
                              <div className="mb-4 w-32 h-32 border-2 border-dashed border-secondary-300 rounded-lg flex items-center justify-center bg-secondary-50">
                                <ImageIcon className="h-12 w-12 text-secondary-400" />
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="input-field"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleUpiQRUpload(file, index);
                                }
                              }}
                            />
                            <p className="mt-1 text-xs text-secondary-500">
                              Upload QR code of UPI ID (max 1MB - JPG, PNG, WEBP)
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Save Button */}
                <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveSection(null)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
                  >
                    <ChevronDown className="h-4 w-4 rotate-90" />
                    Back
                  </button>
                </div>
              </div>
              </>
            )}

            {/* SEO Settings Section */}
            {activeSection === 'seo' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-secondary-900">SEO & Search Settings</h2>
                <div className="group relative">
                  <HelpCircle className="h-4 w-4 text-secondary-400 cursor-help" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-secondary-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                    Customise how your profile appears on Google, Facebook, and other search engines.
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Google Search Preview */}
                <div className="p-4 bg-secondary-50 rounded-xl border border-secondary-200">
                  <p className="text-xs font-medium text-secondary-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Search className="h-3 w-3" /> Search Engine Preview
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-secondary-200 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center border border-secondary-200">
                        <Globe className="h-3 w-3 text-secondary-500" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[12px] text-secondary-900 leading-tight">MyOneQR</span>
                        <span className="text-[10px] text-secondary-500 leading-tight">https://myoneqr.com › {watch('profile_url') || 'your-slug'}</span>
                      </div>
                    </div>
                    <h3 className="text-xl text-[#1a0dab] hover:underline cursor-pointer mb-1 truncate">
                      {watch('seo_title') || (watch('tagline') ? `${watch('name')} — ${watch('tagline')} | MY1QR` : `${watch('name')} | MY1QR`)}
                    </h3>
                    <p className="text-sm text-[#4d5156] line-clamp-2 leading-relaxed">
                      {watch('seo_description') || (watch('bio') ? watch('bio').slice(0, 160) : `Connect with ${watch('name')} on MY1QR. Create your digital profile and share your contact details easily.`)}
                    </p>
                  </div>
                  <p className="mt-3 text-[11px] text-secondary-400 italic">
                    * This is a simulated preview and may vary depending on how search engines index your page.
                  </p>
                </div>

                {/* SEO Title */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <label htmlFor="seo_title" className="block text-sm font-medium text-secondary-700">
                      SEO Meta Title
                    </label>
                    <span className={`text-[10px] font-medium ${(watch('seo_title')?.length || 0) > 60 ? 'text-amber-500' : 'text-secondary-400'}`}>
                      {watch('seo_title')?.length || 0} / 60 chars (recommended)
                    </span>
                  </div>
                  <input
                    {...register('seo_title', { maxLength: 120 })}
                    type="text"
                    id="seo_title"
                    className="input-field"
                    placeholder="e.g. John Doe | Senior Product Designer"
                  />
                  <p className="mt-1.5 text-xs text-secondary-500 leading-relaxed">
                    Appears as the clickable headline in search results. Keep it catchy and under 60 characters for best results.
                  </p>
                </div>

                {/* SEO Description */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <label htmlFor="seo_description" className="block text-sm font-medium text-secondary-700">
                      SEO Meta Description
                    </label>
                    <span className={`text-[10px] font-medium ${(watch('seo_description')?.length || 0) > 160 ? 'text-amber-500' : 'text-secondary-400'}`}>
                      {watch('seo_description')?.length || 0} / 160 chars (recommended)
                    </span>
                  </div>
                  <textarea
                    {...register('seo_description', { maxLength: 200 })}
                    id="seo_description"
                    rows={3}
                    className="input-field"
                    placeholder="A brief summary of your profile to attract visitors from search engines."
                  />
                  <p className="mt-1.5 text-xs text-secondary-500 leading-relaxed">
                    Briefly describe who you are or what you do. This snippet helps people decide whether to click your link.
                  </p>
                </div>

                {/* SEO Keywords */}
                <div>
                  <label htmlFor="seo_keywords" className="block text-sm font-medium text-secondary-700 mb-2">
                    Meta Keywords (comma separated)
                  </label>
                  <input
                    {...register('seo_keywords', { maxLength: 500 })}
                    type="text"
                    id="seo_keywords"
                    className="input-field"
                    placeholder="e.g. designer, developer, digital business card, india"
                  />
                  <p className="mt-1.5 text-xs text-secondary-500 leading-relaxed">
                    Keywords relevant to your profile. (e.g. photography, freelance, portfolio)
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
                >
                  <ChevronDown className="h-4 w-4 rotate-90" />
                  Back
                </button>
              </div>
            </div>
              </>
            )}

            {/* Contact Form Settings */}
            {activeSection === 'contact' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 transition-colors mb-4"
                >
                  <ChevronDown className="h-5 w-5 rotate-90" />
                  <span>Back to Menu</span>
                </button>

            <div className="card">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Contact Form Settings</h2>
              <p className="text-sm text-secondary-600 mb-4">
                Customize the contact form that appears at the end of your public profile
              </p>

              <div className="space-y-4">
                {/* Contact Form Enabled Toggle */}
                <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                  <div>
                    <label htmlFor="contact_form_enabled" className="block text-sm font-medium text-secondary-900 mb-1">
                      Show Contact Form
                    </label>
                    <p className="text-xs text-secondary-600">
                      Toggle to show or hide the contact form on your public profile
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      {...register('contact_form_enabled')}
                      type="checkbox"
                      id="contact_form_enabled"
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-secondary-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {/* Contact Form Title */}
                <div>
                  <label htmlFor="contact_form_title" className="block text-sm font-medium text-secondary-700 mb-2">
                    Contact Form Title
                  </label>
                  <input
                    {...register('contact_form_title')}
                    type="text"
                    id="contact_form_title"
                    className="input-field"
                    placeholder="Contact Me"
                    maxLength={255}
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    Leave empty to use default: "Contact Me"
                  </p>
                </div>

                {/* Contact Form Button Text */}
                <div>
                  <label htmlFor="contact_form_button_text" className="block text-sm font-medium text-secondary-700 mb-2">
                    Button Text
                  </label>
                  <input
                    {...register('contact_form_button_text')}
                    type="text"
                    id="contact_form_button_text"
                    className="input-field"
                    placeholder="Send Message"
                    maxLength={100}
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    Leave empty to use default: "Send Message"
                  </p>
                </div>

                {/* Contact Form Email */}
                <div>
                  <label htmlFor="contact_form_email" className="block text-sm font-medium text-secondary-700 mb-2">
                    Notification Email
                  </label>
                  <input
                    {...register('contact_form_email')}
                    type="email"
                    id="contact_form_email"
                    className="input-field"
                    placeholder="your-email@example.com"
                    maxLength={255}
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    Email address where you'll receive notifications when someone submits the contact form. Leave empty to disable email notifications.
                  </p>
                  {errors.contact_form_email && (
                    <p className="mt-1 text-xs text-red-600">{errors.contact_form_email.message}</p>
                  )}
                </div>

                {/* Predefined Subjects */}
                <div className="border-t border-secondary-200 pt-4">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Predefined Subjects
                  </label>
                  <p className="text-xs text-secondary-500 mb-3">
                    Add common subjects for visitors to choose from when submitting the contact form
                  </p>

                  {/* Add Subject Input */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addSubject();
                        }
                      }}
                      placeholder="Enter a subject (e.g., General Inquiry)"
                      className="input-field flex-1"
                      maxLength={100}
                    />
                    <button
                      type="button"
                      onClick={addSubject}
                      disabled={!newSubject.trim()}
                      className="btn-primary px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Subject Chips */}
                  {contactFormSubjects.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {contactFormSubjects.map((subject) => (
                        <div
                          key={subject}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm border border-primary-200"
                        >
                          <span>{subject}</span>
                          <button
                            type="button"
                            onClick={() => removeSubject(subject)}
                            className="hover:text-red-500 transition-colors"
                          >
                            <CloseIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-secondary-400 italic">
                      No predefined subjects. Visitors will enter their own subject.
                    </p>
                  )}
                </div>

                {/* Auto-popup Delay */}
                <div className="border-t border-secondary-200 pt-4">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Auto-popup Delay
                  </label>
                  <p className="text-xs text-secondary-500 mb-3">
                    Contact form will automatically pop up after this delay when visitors view your profile
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-secondary-500 w-8">5s</span>
                      <input
                        type="range"
                        min="5"
                        max="15"
                        value={popupDelay}
                        onChange={(e) => updatePopupDelay(parseInt(e.target.value, 10))}
                        className="flex-1 h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                      />
                      <span className="text-sm text-secondary-500 w-8">15s</span>
                    </div>
                    <div className="text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-sm font-medium">
                        {popupDelay} seconds
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection(null)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-colors"
                >
                  <ChevronDown className="h-4 w-4 rotate-90" />
                  Back
                </button>
              </div>
            </div>
              </>
            )}

            {/* View Profile and Install App buttons - Only show on main menu */}
            {activeSection === null && (
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
              {/* View Profile button - Desktop */}
              {primaryProfileUrl && (
                <a
                  href={primaryProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="order-2 md:order-1 hidden md:inline-flex items-center justify-center gap-2 px-6 py-3 text-lg bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  <QrCodeIcon className="h-5 w-5" />
                  <span className="whitespace-nowrap">View Profile</span>
                </a>
              )}
              {/* Install App Button - Desktop - Only for Basic and Premium plans */}
              {isInstallable && isPaidPlan && (
                <button
                  type="button"
                  onClick={handleInstallClick}
                  className="order-3 hidden md:inline-flex items-center justify-center gap-2 px-6 py-3 text-lg bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                >
                  <Download className="h-5 w-5" />
                  <span className="whitespace-nowrap">Install App</span>
                </button>
              )}
            </div>
            )}
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* View Profile and Install App buttons - Only show on main menu */}
          {activeSection === null && (
            <>
              {/* View Profile button - Mobile only */}
              {primaryProfileUrl && (
                <a
                  href={primaryProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hidden inline-flex items-center justify-center gap-2 px-6 py-3 text-lg bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors w-full"
                >
                  <QrCodeIcon className="h-5 w-5" />
                  <span className="whitespace-nowrap">View Profile</span>
                </a>
              )}

              {/* Install App Button - Mobile only - Only for Basic and Premium plans */}
              {isInstallable && isPaidPlan && (
                <button
                  type="button"
                  onClick={handleInstallClick}
                  className="md:hidden inline-flex items-center justify-center gap-2 px-6 py-3 text-lg bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors w-full"
                >
                  <Download className="h-5 w-5" />
                  <span className="whitespace-nowrap">Install App</span>
                </button>
              )}
            </>
          )}

          {/* Mobile Preview removed intentionally */}
        </div>
      </div >

      {/* Image Cropper Modal */}
      {
        showCropper && croppedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Crop Profile Image</h3>
                  <button
                    onClick={() => setShowCropper(false)}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <CloseIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <div className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden">
                    <Cropper
                      image={croppedImage.preview}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onCropComplete={(_, croppedAreaPixels) => {
                        setCroppedAreaPixels(croppedAreaPixels);
                      }}
                      onZoomChange={setZoom}
                      showGrid={true}
                      style={{
                        containerStyle: {
                          width: '100%',
                          height: '100%',
                          position: 'relative'
                        }
                      }}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <span className="text-sm font-medium">Zoom:</span>
                      <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="w-24"
                      />
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCropper(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropComplete}
                    disabled={!imageLoaded || !croppedAreaPixels}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Crop & Upload
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* App Selector Modal */}
      <AppSelectorModal
        isOpen={showAppSelector}
        onClose={() => setShowAppSelector(false)}
        onSelectApp={(app) => {
          addCustomLink(app);
        }}
        onSelectCustom={() => {
          setShowAppSelector(false);
          setShowCustomAppSelector(true);
        }}
      />

      {/* Custom App Selector Modal */}
      <CustomAppSelector
        isOpen={showCustomAppSelector}
        onClose={() => setShowCustomAppSelector(false)}
        onAdd={(label, iconName) => {
          addCustomLink(undefined, label, iconName);
        }}
      />
    </div >
  );
};

export default Profile;
